import React, { useMemo, useState } from 'react';
import * as wasm from 'sched';

interface AvailabilityHeatmapProps {
  result: wasm.PlanningGridResult;
}

const CELL = 12;
const GAP = 2;
const STEP = CELL + GAP;
const NAME_W = 64;
/** Period-label header height (matches the inline 14 below). */
const HEADER_H = 14;
/** Per-row height — cell + the parent's `space-y-0.5` (2px) gap. */
const ROW_H = CELL + 2;

export const AvailabilityHeatmap: React.FC<AvailabilityHeatmapProps> = ({ result }) => {
  const days: any[] = (result as any).days ?? [];
  const periods: any[] = (result as any).periods ?? [];
  const people = result.people;

  const personEntries = useMemo(
    () => Object.entries(people).sort(([, a], [, b]) => a.name.localeCompare(b.name)),
    [people],
  );

  const today = new Date().toISOString().slice(0, 10);

  // Lifted hover state. The tooltip is rendered as a sibling of the
  // `overflow-hidden` wrapper so it isn't clipped by the panel's right-edge
  // fade. Per-row hover handlers populate this; clearing it hides the
  // tooltip.
  const [hovered, setHovered] = useState<{ personIdx: number; dayIdx: number } | null>(null);

  if (personEntries.length === 0 || days.length === 0) return null;

  // Precompute period label positions in pixels
  const periodLabels = useMemo(() => {
    return periods.map((period: any, pi: number) => {
      const pDays = days.filter((d: any) => d.period_idx === pi);
      if (pDays.length === 0) return null;
      const firstIdx = days.indexOf(pDays[0]);
      return {
        leftPx: firstIdx * STEP,
        widthPx: pDays.length * STEP - GAP,
        label: period.label,
      };
    }).filter(Boolean) as { leftPx: number; widthPx: number; label: string }[];
  }, [days, periods]);

  const totalWidthPx = days.length * STEP - GAP;

  const tooltipNode = (() => {
    if (!hovered) return null;
    const entry = personEntries[hovered.personIdx];
    if (!entry) return null;
    const [, person] = entry;
    const d = days[hovered.dayIdx];
    const cell = person.cells[hovered.dayIdx.toString()];
    const cap = cell?.capacity ?? 0;
    const utilized = cell?.utilized ?? 0;
    const allocPct = cap > 0 ? Math.round((utilized / cap) * 100) : 0;
    // Position the tooltip just above the hovered row inside the heatmap's
    // coordinate system. The horizontal anchor is the cell's left edge so it
    // visually traces back to the source.
    const left = NAME_W + hovered.dayIdx * STEP;
    const rowTop = HEADER_H + hovered.personIdx * ROW_H;
    return (
      <div
        className="absolute z-30 px-2 py-1 rounded bg-black/90 text-white text-[10px] shadow-lg whitespace-nowrap pointer-events-none"
        style={{ left, top: rowTop - 22 }}
      >
        <span className="font-medium">{d.label}</span>
        <span className="text-white/60"> · </span>
        <span>{cap * 10}% available</span>
        {utilized > 0 && (
          <>
            <span className="text-white/60"> · </span>
            <span>{allocPct}% allocated</span>
          </>
        )}
      </div>
    );
  })();

  return (
    <div className="space-y-0.5 relative">
      {/* Clipped area with right fade — child of the relative wrapper above
          so the tooltip (a sibling) sits in the same coordinate system but
          isn't subject to this overflow-hidden. */}
      <div className="relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-cm-bg-surface to-transparent z-10 pointer-events-none" />

        {/* Period labels */}
        <div className="flex">
          <div style={{ width: NAME_W }} className="shrink-0" />
          <div className="relative" style={{ width: totalWidthPx, height: HEADER_H }}>
            {periodLabels.map((pl, i) => (
              <div
                key={i}
                className="absolute top-0 text-[8px] text-cm-text-muted/60 truncate"
                style={{ left: pl.leftPx, width: pl.widthPx }}
              >
                {pl.label}
              </div>
            ))}
          </div>
        </div>

        {/* Person rows */}
        {personEntries.map(([personId, person], idx) => (
          <HeatmapRow
            key={personId}
            person={person}
            days={days}
            today={today}
            onHover={(dayIdx) => setHovered({ personIdx: idx, dayIdx })}
            onLeave={() => setHovered(null)}
          />
        ))}
      </div>

      {/* Tooltip — rendered OUTSIDE the overflow-hidden wrapper so it can
          extend beyond the panel boundary. Coordinates are computed relative
          to this same `relative` parent. */}
      {tooltipNode}

      {/* Legend */}
      <div className="flex items-center gap-1.5 text-[8px] text-cm-text-muted/50 pt-0.5" style={{ paddingLeft: NAME_W }}>
        <span>Unavailable</span>
        {[0.1, 0.3, 0.5, 0.7, 1.0].map((o, i) => (
          <div key={i} className="rounded-[2px]" style={{ width: CELL, height: CELL, backgroundColor: `rgba(255, 255, 255, ${o})` }} />
        ))}
        <span>Available</span>
        <span className="ml-3">Free</span>
        {[0, 0.5, 1.0].map((ratio, i) => {
          const r = Math.round(255 - ratio * 156);
          const g = Math.round(255 - ratio * 153);
          const b = Math.round(255 - ratio * 14);
          return <div key={i} className="rounded-[2px]" style={{ width: CELL, height: CELL, backgroundColor: `rgb(${r}, ${g}, ${b})` }} />;
        })}
        <span>Allocated</span>
      </div>
    </div>
  );
};

function HeatmapRow({ person, days, today, onHover, onLeave }: {
  person: wasm.PersonPlanDto;
  days: any[];
  today: string;
  onHover: (dayIdx: number) => void;
  onLeave: () => void;
}) {
  return (
    <div className="flex items-center">
      <div style={{ width: NAME_W }} className="shrink-0 text-[10px] text-cm-text-muted truncate pr-1">{person.name}</div>
      <div className="flex relative" style={{ gap: GAP }}>
        {days.map((d: any, i: number) => {
          const cell = person.cells[i.toString()];
          const cap = cell?.capacity ?? 0;
          const utilized = cell?.utilized ?? 0;
          const isToday = d.date === today;

          const opacity = cap / 10;
          const allocRatio = cap > 0 ? Math.min(utilized / cap, 1) : 0;
          const r = Math.round(255 - allocRatio * 156);
          const g = Math.round(255 - allocRatio * 153);
          const b = Math.round(255 - allocRatio * 14);

          return (
            <div
              key={i}
              className={`shrink-0 rounded-[2px] ${isToday ? 'ring-1 ring-red-400' : ''}`}
              style={{ width: CELL, height: CELL, backgroundColor: `rgba(${r}, ${g}, ${b}, ${opacity})` }}
              onMouseEnter={() => onHover(i)}
              onMouseLeave={onLeave}
            />
          );
        })}
      </div>
    </div>
  );
}
